<template>
	<Flyout
		:title="titleRoleFlyout"
		:parentTag="'form'"
		:display="display"
		:novalidate="'novalidate'"
		@show="closeRoleSettingFlyout"
	>
		<template #flyout-content>
			<div
				class="role-settings"
				:class="[
					isExternalRoleType
						? 'role-settings--consultant'
						: isInternalRoleType
						? 'role-settings--internal'
						: ''
				]"
			>
				<BlockNotifications
					:title="
						editionMode
							? 'Role Update Error'
							: 'Role Creation Error'
					"
					:message="
						`Unable to ${
							editionMode ? 'update' : 'create'
						} the role due to internal Error. Please try again or contact support to resolve the error. ${
							errorMessage ? errorMessage : ''
						}`
					"
					:blockState="'error'"
					v-if="showNotificationError"
					ref="blockErrorContainer"
				/>

				<div class="role-settings__header">
					<div class="role-settings__role-name">
						<span
							class="role-settings__role-name-static"
							v-if="editionMode"
							>{{ roleModal.role }}</span
						>
						<Input
							ariaLabel="Aria Label"
							className="field__input"
							:label="inputLabel"
							name="Name"
							:helpText="'Name cannot be edited once created.'"
							:required="true"
							:placeholder="'Enter uniques role name'"
							v-model="roleModal.role"
							:forceCustomErrorMessage="forceErrorMessage"
							@input="setRole"
							:maxLength="100"
							v-else
						/>
					</div>
					<div class="role-settings__header-toggle">
						<TooltipContainer
							:tooltip="
								isDisableActiveDeactiveRole
									? 'You cannot disable your own role.'
									: ''
							"
							:position="'bottom-end'"
						>
							<template #container>
								<Toggle
									:label="
										roleModal.active ? 'Active' : 'Inactive'
									"
									:disabled="isDisableActiveDeactiveRole"
									v-model="roleModal.active"
									ref="tooltipActiveDeactiveRef"
									@mouseenter.native="
										tooltipActiveDeactiveVisible = true
									"
									@focus.native="
										tooltipActiveDeactiveVisible = true
									"
									@mouseleave.native="
										tooltipActiveDeactiveVisible = false
									"
									@blur.native="
										tooltipActiveDeactiveVisible = false
									"
									:offset="[0, 4]"
									position="left"
								/>
							</template>
						</TooltipContainer>
					</div>
				</div>
				<div
					class="role-settings__customers-sets"
					v-if="isExternalRoleType"
				>
					<Heading
						:title="'Client Access Permission'"
						:variant="'medium'"
						:showIcone="false"
					/>
					<SelectList
						:title="'Clients'"
						placeholder="Search by client name or number"
						:itemAvailables="availableCustomers"
						:itemSelected="roleModal.customers"
						:mapping="mappingFields"
						@change="setSelectedCustomers"
						:displayUnSelectAllButton="false"
					>
					</SelectList>
				</div>

				<div
					class="role-settings__permission-sets styled-container"
					v-if="isInternalRoleType"
				>
					<p class="styled-container__title">
						Permission sets
					</p>
					<div class="permission-sets">
						<div class="permission-sets__search">
							<Search
								v-model="search"
								variant="toggle-icons"
								placeholder="Search permissions"
								:label="null"
							/>
						</div>
						<div
							class="permission-sets__clear-all"
							v-show="showClearAllButton"
						>
							<Button
								labelText="Clear All"
								:btnClick="reset"
								:className="'btn--link'"
							/>
						</div>
						<ul class="permission-sets__items">
							<!-- Dev: get the array of permission and fill in this list. This is just for testing purposes.
                -->
							<li
								class="permission-sets__item"
								v-for="(permission,
								index) in filteredPermissions"
								:key="index"
							>
								<Fieldset
									:legend="
										filteredPermissions[index].groupName
									"
									variant="fieldset--full-width"
								>
									<template #fieldset-content>
										<CheckboxGroup
											:checkedValues="checkedpermissions"
											@change="changeValue"
											:displayCheckAll="true"
											:items="
												generateItems(
													permission.permissions
												)
											"
										/>
									</template>
								</Fieldset>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</template>

		<template #flyout-footer>
			<ButtonGroup
				:items="[
					{
						btnClick: saveAction,
						className: 'btn--primary',
						labelText: editionMode ? 'Update' : 'Create',
						disabled: disabledButton
					},
					{
						btnClick: cancelAction,
						className: 'btn btn--link',
						labelText: 'Cancel'
					}
				]"
			/>
		</template>
	</Flyout>
</template>

<script>
import Flyout from '@/components/shells/flyout/flyout';
import Input from '@/components/forms/input/input';
import Fieldset from '@/components/forms/fieldset/fieldset';
import Search from '@/components/forms/search/search';
import CheckboxGroup from '@/components/forms/selection-control/checkbox/checkbox-group';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import { mapActions, mapGetters } from 'vuex';
import { numericSortingArray, filterAndSortArray } from '@/utils/function';
import { required, maxLength } from 'vuelidate/lib/validators';
import Toggle from '@/components/forms/selection-control/toggle/toggle.vue';
import TooltipContainer from '@/components/shells/tooltip/tooltip-container.vue';
import SelectList from '@/components/forms/selection-list/selection-list';
import Heading from '@/components/visual/heading.vue';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';
import { CUSTOMER_OPTIONS } from '@/sections/options/options-mutation-type';
import CustomerOptionsMixin from '@/mixins/customers/customer-options';
import { compareArraysOfObjects } from '@/utils/function';
import { deepEqual as objCompare } from '@/utils/object';
import Vue from 'vue';
import _ from 'lodash';

export default {
	name: 'RoleAddEditFlyout',
	mixins: [CustomerOptionsMixin],
	components: {
		Flyout,
		Input,
		Fieldset,
		Search,
		CheckboxGroup,
		ButtonGroup,
		Button,
		Toggle,
		TooltipContainer,
		SelectList,
		Heading,
		BlockNotifications
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		display: {
			type: Boolean,
			default: false
		},
		roleData: {
			type: Object,
			default: () => {}
		}
	},
	validations: {
		roleModal: {
			role: {
				required,
				maxLength: maxLength(100)
			}
		}
	},
	data() {
		return {
			title: '',
			search: '',
			roleModal: {},
			disabledButton: true,
			checkedpermissions: [],
			selectedCustomers: [],
			hasSameCustomers: true,
			showNotificationError: false,
			forceErrorMessage: '',
			errorMessage: '',
			tooltipActiveDeactiveVisible: false,
			tooltipActiveDeactiveRefMounted: null
		};
	},
	mounted() {
		this.tooltipActiveDeactiveRefMounted = this.$refs[
			'tooltipActiveDeactiveRef'
		];
	},
	computed: {
		...mapGetters('roles', ['permissions', 'roles']),
		...mapGetters('roles', ['permissionIdByPermissionCode']),
		filteredPermissions: function() {
			let result = filterAndSortArray(
				this.permissions.items,
				{
					groupName: this.search.toLowerCase()
				},
				null
			);
			result &&
				result.forEach(group => {
					group.permissions.forEach(permission => {
						permission.permission = permission.permissionCode.split(
							'.'
						)[1];
					});
				});

			return result;
		},
		editionMode() {
			return this.roleModal && this.roleModal.roleId > 0;
		},
		isInternalRoleType() {
			return this.roleModal.clientId === 'vue-support-portal';
		},
		isExternalRoleType() {
			return this.roleModal.clientId === 'vue-support-external';
		},
		titleRoleFlyout() {
			if (this.editionMode) {
				return this.isInternalRoleType
					? 'Edit Internal Role'
					: this.isExternalRoleType
					? 'Edit Consultant Role'
					: '';
			} else {
				return this.isInternalRoleType
					? 'Create New Internal Role'
					: this.isExternalRoleType
					? 'Create New Consultant Role'
					: '';
			}
		},
		inputLabel() {
			return this.isInternalRoleType
				? 'Internal Role Name'
				: this.isExternalRoleType
				? 'Consultant Role Name'
				: '';
		},
		customers() {
			const { items } = this[CUSTOMER_OPTIONS];
			return items || [];
		},
		availableCustomers() {
			if (!this.customers.length) return [];
			return this.customers.map(item => {
				return {
					text: item.text,
					value: item.value
				};
			});
		},
		mappingFields() {
			return {
				id: 'value',
				name: 'text'
			};
		},
		hasRoleCHanges() {
			const areEqualsObj = objCompare(
				{ ...this.roleData },
				{ ...this.roleModal },
				true,
				['permissions', 'customers']
			);

			return !areEqualsObj;
		},
		showClearAllButton() {
			const containsAtLeastOne = permissionIds => {
				if (!_.isEmpty(this.filteredPermissions)) {
					for (let group of this.filteredPermissions) {
						const permissionIdSet = new Set(
							group.permissions.map(
								permission => permission.permissionId
							)
						);
						// Check if at least one of the permissionIds is in the set
						for (let id of permissionIds) {
							if (permissionIdSet.has(id)) {
								return true; // Return true if at least one is found
							}
						}
					}
				}

				// If none are found, return false
				return false;
			};
			return containsAtLeastOne(this.checkedpermissions);
		},
		isDisableActiveDeactiveRole() {
			const user = JSON.parse(localStorage.getItem('oidcUser'));
			const { SupportRole: oidcRolesUser } = user;

			return (
				!_.isEmpty(oidcRolesUser) &&
				oidcRolesUser.includes(this.roleModal.role)
			);
		}
	},
	methods: {
		...mapActions('roles', {
			saveRoleAction: 'saveRole'
		}),
		generateItems(permissions) {
			let perms = [];

			permissions.forEach(p => {
				perms[p.permission] = p.permissionId;
			});
			let checkboxLabels = ['create', 'read', 'update', 'delete'];

			let listcheckGroup = [];

			checkboxLabels.forEach(element => {
				listcheckGroup.push({
					label: element,
					value: perms[element],
					disabled: perms[element] === undefined
				});
			});
			return listcheckGroup;
		},
		closeRoleSettingFlyout(e) {
			this.cancelAction(e);
		},
		async saveAction() {
			let role = _.omit(this.roleModal, ['customers', 'permissions']);

			if (!_.isEmpty(this.checkedpermissions)) {
				const permissions = this.checkedpermissions.map(id => ({
					permissionId: id
				}));
				Object.assign(role, { permissions });
			}

			if (!role.roleDescription) {
				Object.assign(role, { roleDescription: role.role });
			}

			if (this.isExternalRoleType) {
				const clientUpdatePermission = this.permissionIdByPermissionCode(
					'clients.update'
				);
				if (
					!role.permissions.some(
						permission =>
							permission.permissionId === clientUpdatePermission
					)
				) {
					role.permissions.push({
						permissionId: clientUpdatePermission
					});
				}

				if (!_.isEmpty(this.roleModal.customers)) {
					const customers = this.roleModal.customers.map(
						customer => ({
							customerId: customer.value
						})
					);
					Object.assign(role, { customers });
				}
			}

			await this.saveRoleAction(role).then(response => {
				const { isSuccessful } = response;

				if (isSuccessful) {
					this.cancelAction();
				}
				if (!isSuccessful) {
					const { statusCode, message } = response;
					if (statusCode === 409) {
						this.forceErrorMessage = message;
						this.disabledButton = true;
					} else {
						this.errorMessage = message;
						this.showNotificationError = true;
					}
				}
			});
		},
		cancelAction(e) {
			if (e) e.preventDefault();
			if (!_.isEmpty(this.checkedpermissions)) {
				this.checkedpermissions = [];
			}
			if (this.showNotificationError) {
				this.showNotificationError = false;
			}
			if (this.search) {
				this.search = '';
			}

			this.$emit('show', false);
		},
		reset(e) {
			if (e) e.preventDefault();
			this.search = '';
			this.checkedpermissions = [];
		},
		compare(array1, array2) {
			let array1Sorted = numericSortingArray(array1);
			let array2Sorted = numericSortingArray(array2);
			return this.arrayEquals(array1Sorted, array2Sorted);
		},
		arrayEquals(a, b) {
			return (
				Array.isArray(a) &&
				Array.isArray(b) &&
				a.length === b.length &&
				a.every((val, index) => val === b[index])
			);
		},
		changeValue(checkedPermissions) {
			//}, added1, removed1) {
			checkedPermissions = checkedPermissions.map(Number);
			let checkedPerms = _.uniq(checkedPermissions);
			/*
			let added = parseInt(added1, 10);
			let removed = parseInt(removed1, 10);

			if (added !== undefined && added === null) {
				this.filteredPermissions.forEach(group => {
					let permissionFound = group.permissions.find(
						x => x.permissionId === added
					);
					if (permissionFound) {
						let readPermission, updatePermission, createPermission;

						switch (permissionFound.permission) {
							case 'create':
								// add: read, update
								readPermission =
									group.permissions.find(
										x => x.permission === 'read'
									) || {};
								if (
									checkedPerms.indexOf(
										readPermission.permissionId
									) === -1
								)
									checkedPerms.push(
										readPermission.permissionId
									);

								updatePermission =
									group.permissions.find(
										x => x.permission === 'update'
									) || {};
								if (
									checkedPerms.indexOf(
										updatePermission.permissionId
									) === -1
								)
									checkedPerms.push(
										updatePermission.permissionId
									);

								break;
							case 'update':
								// add: read
								readPermission =
									group.permissions.find(
										x => x.permission === 'read'
									) || {};
								if (
									checkedPerms.indexOf(
										readPermission.permissionId
									) === -1
								)
									checkedPerms.push(
										readPermission.permissionId
									);

								break;
							case 'read':
								// do nothing
								break;
							case 'delete':
								// add: create, read, update
								readPermission =
									group.permissions.find(
										x => x.permission === 'read'
									) || {};
								if (
									checkedPerms.indexOf(
										readPermission.permissionId
									) === -1
								)
									checkedPerms.push(
										readPermission.permissionId
									);

								updatePermission =
									group.permissions.find(
										x => x.permission === 'update'
									) || {};
								if (
									checkedPerms.indexOf(
										updatePermission.permissionId
									) === -1
								)
									checkedPerms.push(
										updatePermission.permissionId
									);

								createPermission =
									group.permissions.find(
										x => x.permission === 'create'
									) || {};
								if (
									checkedPerms.indexOf(
										createPermission.permissionId
									) === -1
								)
									checkedPerms.push(
										createPermission.permissionId
									);

								break;
							default:
							// do nothing
						}
					}
				});
			}
			if (removed !== undefined && added === null) {
				this.filteredPermissions.forEach(group => {
					let permissionFound = group.permissions.find(
						x => x.permissionId === removed
					);
					if (permissionFound) {
						let deletePermission,
							deleteIndex,
							createPermission,
							createIndex,
							updatePermission,
							udpateIndex;
						switch (permissionFound.permission) {
							case 'create':
								// remove: delete
								deletePermission =
									group.permissions.find(
										x => x.permission === 'delete'
									) || {};
								deleteIndex = checkedPerms.indexOf(
									deletePermission.permissionId
								);
								if (deleteIndex !== -1)
									checkedPerms.splice(deleteIndex, 1);

								break;
							case 'update':
								// remove: create, delete
								createPermission =
									group.permissions.find(
										x => x.permission === 'create'
									) || {};
								createIndex = checkedPerms.indexOf(
									createPermission.permissionId
								);
								if (createIndex !== -1)
									checkedPerms.splice(createIndex, 1);

								deletePermission =
									group.permissions.find(
										x => x.permission === 'delete'
									) || {};
								deleteIndex = checkedPerms.indexOf(
									deletePermission.permissionId
								);
								if (deleteIndex !== -1)
									checkedPerms.splice(deleteIndex, 1);

								break;
							case 'read':
								// remove: create, update, delete
								createPermission =
									group.permissions.find(
										x => x.permission === 'create'
									) || {};
								createIndex = checkedPerms.indexOf(
									createPermission.permissionId
								);
								if (createIndex !== -1)
									checkedPerms.splice(createIndex, 1);

								deletePermission =
									group.permissions.find(
										x => x.permission === 'delete'
									) || {};
								deleteIndex = checkedPerms.indexOf(
									deletePermission.permissionId
								);
								if (deleteIndex !== -1)
									checkedPerms.splice(deleteIndex, 1);

								updatePermission =
									group.permissions.find(
										x => x.permission === 'update'
									) || {};
								udpateIndex = checkedPerms.indexOf(
									updatePermission.permissionId
								);
								if (udpateIndex !== -1)
									checkedPerms.splice(udpateIndex, 1);

								break;
							case 'delete':
								// do nothing
								break;
							default:
							// do nothing
						}
					}
				});
			}
			*/
			this.checkedpermissions = checkedPerms;
		},
		setRole(value) {
			this.roleModal.role = value;
			this.$v.roleModal.role.$touch();
		},
		isSaveUpdateButtonDisabled() {
			const {
				checkedpermissions,
				hasSameCustomers,
				hasRoleCHanges,
				roleModal: { role, permissions, customers },
				roleData: { permissions: roleDataPermissions }
			} = this;

			if (!role || (this.$v && this.$v.$anyError)) {
				return true;
			}

			if (this.isInternalRoleType && _.isEmpty(permissions)) {
				return true;
			}
			if (this.isExternalRoleType && _.isEmpty(customers)) {
				return true;
			}

			if (this.editionMode) {
				if (
					this.isInternalRoleType &&
					this.roleData.role === role &&
					!hasRoleCHanges
				) {
					return this.compare(
						checkedpermissions,
						roleDataPermissions || []
					);
				}

				if (
					this.isExternalRoleType &&
					hasSameCustomers &&
					!hasRoleCHanges
				) {
					return true;
				}
			}

			return false;
		},
		setSelectedCustomers(customers) {
			Vue.set(this, 'selectedCustomers', customers);
			Vue.set(this.roleModal, 'customers', customers);
		},
		compareCustomers() {
			const {
				selectedCustomers,
				editionMode,
				roleData: { customers: roleDataCustomers }
			} = this;
			if (editionMode) {
				return compareArraysOfObjects(
					selectedCustomers,
					roleDataCustomers
				);
			}
			return selectedCustomers.length === 0;
		}
	},
	watch: {
		display: {
			immediate: true,
			handler(val) {
				if (!val) {
					this.roleModal = {};
					this.checkedpermissions = [];
				}
			}
		},
		roleData: {
			deep: true,
			handler(obj) {
				const clientReadPermission = this.permissionIdByPermissionCode(
					'clients.read'
				);

				if (obj.roleId) {
					this.validatePermission(['roles.update'], true);
					this.roleModal = JSON.parse(JSON.stringify(obj));

					//check if role has client read permission
					if (!_.includes(obj.permissions, clientReadPermission)) {
						this.roleModal.permissions.push(clientReadPermission);
					}

					if (!_.isEmpty(this.roleData.customers)) {
						Vue.set(
							this,
							'selectedCustomers',
							this.roleData.customers
						);
					}
				} else {
					this.validatePermission(['roles.create'], true);

					this.roleModal = {
						...obj,
						active: true,
						permissions: [clientReadPermission]
					};
				}
				this.checkedpermissions = [...this.roleModal.permissions] || [];
			}
		},
		roleModal: {
			deep: true,
			handler() {
				if (!_.isEmpty(this.roleModal.customers)) {
					this.hasSameCustomers = this.compareCustomers();
				}

				this.disabledButton = this.isSaveUpdateButtonDisabled();
			}
		},
		'roleModal.role': {
			handler() {
				if (this.forceErrorMessage) {
					this.$nextTick(() => {
						this.forceErrorMessage = '';
					});
				}
			}
		},
		checkedpermissions() {
			this.roleModal.permissions = this.checkedpermissions;
			if (!this.editionMode) {
				this.disabledButton = this.isSaveUpdateButtonDisabled();
			}
		}
	}
};
</script>

<style lang="scss">
.role-settings {
	.block-notify {
		margin-bottom: $gutter-xl;
	}
	&--consultant {
		width: 410px;
		@include mq($from: lg) {
			width: 750px;
		}
	}
	&--internal {
		width: 410px;
		@include mq($from: lg) {
			width: 900px;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		gap: $gutter-lg;
		justify-content: space-between;
		margin-bottom: $gutter-xl;
		margin-top: $gutter-sm;

		&-secondary {
			display: flex;
			align-items: center;
			&-toggle + &-tooltip {
				display: none;
			}

			&-toggle:hover + &-tooltip {
				display: inline;
			}
		}
	}

	&__role-name-static {
		font-size: $default-font-size;
		font-weight: $medium-font-weight;
		word-break: break-word;
		text-transform: capitalize;
	}

	&__role-name .field__input {
		min-width: 250px;
	}
}

.permission-sets {
	display: flex;
	flex-wrap: wrap;
	padding: $padding-md;
	max-height: calc(100vh - 234px);
	overflow: auto;

	&__search {
		width: 100%;
		margin-bottom: $gutter-xl;
	}

	&__clear-all {
		margin: $gutter-md 0 $gutter-md auto;
	}

	&__items {
		width: 100%;
		display: grid;
		column-gap: $gutter-xl;
		row-gap: $gutter-lg;
		margin: 0;
		padding: 0;

		@include mq($from: md) {
			grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
		}
	}

	&__item {
		list-style: none;

		.fieldset .fieldset__body {
			> * {
				margin: 0;
			}

			.checkbox-group {
				display: grid;
				width: 100%;

				@include mq($from: md) {
					grid-template-columns: 1fr 1fr 1fr;
					grid-auto-flow: initial;
				}

				@include mq($from: lg) {
					grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
				}
			}
		}

		.fieldset .checkbox__label {
			text-transform: capitalize;
		}
	}
}

/*------------------------------------*\
  #BASE MOBILE LANDSCAPE-UP
\*------------------------------------*/
@include mq($from: 450px) {
	/* .role-settings {
		&__role-name {
			max-width: 210px;
		}
	} */

	.permission-sets {
		&__search {
			max-width: 210px;
			order: 0;
		}

		&__clear-all {
			margin: 0 0 $gutter-xl auto;
			order: 0;
		}
	}
}
</style>
